"use client";
import React from "react";
import { useTranslations } from "next-intl";
import { BorderedButton } from "../button/borderedButton";
import { ConfirmButton } from "../button/confirmButton";
import { usePathname } from "next/navigation";
import { clearCart, clearServiceCart } from "@/utils/cartUtils";
import CloseIcon from "../_icons/closeIcon";
import { removeFromLocalStorage } from "@/utils/localStorageUtils";
import { LocalStorageKey } from "@/utils/storageKeys";
import { RegularModal } from "../modals/modals";
import useMediaQuery from "@/utils/useMediaQuery";
import { lightenColor } from "@/utils/lightenColor";
import { isEmpty } from "lodash";

export interface clearCartAction {
  text: string;
  onClick: () => void;
  type: "primary" | "secondary";
}
interface ClearCartModalProps {
  isOpen: boolean;
  onRequestClose: () => void;
  merchantColor: string;
  addToCart?: () => void;
  actions?: clearCartAction[];
  title?: string;
  message?: string;
}

export const ClearCartModal = ({
  isOpen,
  onRequestClose,
  merchantColor,
  addToCart,
  actions,
  title,
  message,
}: ClearCartModalProps) => {
  const t = useTranslations("ClearCartModal");
  const pathname = usePathname();
  const isMobileScreen = useMediaQuery("(max-width: 640px)");

  const isServicePage: boolean = pathname?.includes("services") || false;

  const handleCartClear = () => {
    if (isServicePage) {
      clearCart();
      removeFromLocalStorage(LocalStorageKey.GIFT_CARD);
    } else {
      clearServiceCart();
    }
    if (typeof addToCart === "function") {
      addToCart();
    }
    onRequestClose();
  };

  const defaultActions: clearCartAction[] = [
    {
      text: t("clearCart"),
      onClick: handleCartClear,
      type: "secondary",
    },
    {
      text: t("goBack"),
      onClick: onRequestClose,
      type: "primary",
    },
  ];

  const modalActions = !isEmpty(actions) ? actions : defaultActions;

  return (
    <RegularModal
      isOpen={isOpen}
      onRequestClose={onRequestClose}
      overlayClassName="bg-opacity-75"
    >
      <div className="bg-white sm:rounded-lg shadow-lg w-[100%] h-[100%] sm:h-auto sm:w-full max-w-md md:max-w-lg lg:max-w-xl mx-auto overflow-hidden">
        <div className="flex justify-between items-start px-4 pt-4">
          <h2 className="text-2xl font-semibold sm:font-medium text-[#373737]">
            {title ||
              t("h2Title", {
                type: isServicePage ? t("retailItems") : t("services"),
              })}
          </h2>
          <button
            onClick={onRequestClose}
            className="text-gray-600 hover:text-gray-900"
          >
            <CloseIcon />
          </button>
        </div>
        <div className="pt-4 px-4">
          <p className="text-[15px] text-md text-[#000000]">
            {message ||
              t("message", {
                type: !isServicePage ? t("item") : t("service"),
                action: isServicePage
                  ? t("checkoutItem")
                  : t("bookAppointment"),
              })}
          </p>
        </div>
        <div className="sm:static fixed bottom-0 left-0 w-full z-50 sm:z-auto">
          <div className=" flex flex-col-reverse sm:flex-row justify-end sm:gap-4 items-center sm:p-4 max-w-md md:max-w-lg lg:max-w-xl mx-auto bg-white">
            {modalActions?.map((action, index) => {
              const { type, text, onClick } = action;
              return type === "primary" ? (
                <ConfirmButton
                  key={index}
                  style={{
                    backgroundColor: !isMobileScreen
                      ? merchantColor
                      : lightenColor(merchantColor, 90),
                    color: !isMobileScreen ? "white" : "black",
                  }}
                  onClick={onClick}
                  className="w-full sm:w-auto h-16 sm:h-auto text-black sm:text-white disabled:text-black py-2 rounded-none sm:rounded disabled:bg-[#E5E5E5]"
                >
                  {text}
                </ConfirmButton>
              ) : (
                <div key={index} className="w-full sm:w-auto">
                  <BorderedButton
                    style={{
                      color: !isMobileScreen ? merchantColor : "white",
                      border: !isMobileScreen
                        ? `solid ${merchantColor}`
                        : "none",
                      backgroundColor: !isMobileScreen
                        ? "white"
                        : merchantColor,
                    }}
                    className="w-full flex justify-center h-16 sm:h-auto rounded-none sm:rounded"
                    onClick={onClick}
                  >
                    {text}
                  </BorderedButton>
                </div>
              );
            })}
          </div>
        </div>
      </div>
    </RegularModal>
  );
};
